import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  getInvoices, getInvoice,
  cancelInvoice, reverseInvoiceToO2c, recordInvoicePayment, disputeInvoice,
  createRevenueSchedule, getRevenueSchedulesByInvoice, getRevenueSchedules,
  postRevenueEntry, reverseRevenueSchedule, approveJournalEntry,
  reprocessSubscriptionRevRec,
  getAttachments, downloadAttachment,
} from '../services/api';
import { formatCurrency, formatDate, formatDateTime, StatusBadge, creditNoteTaxableAmount, invoiceTaxableAmount } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import {
  ChevronRight, X, DollarSign, AlertOctagon,
  TrendingUp, Calendar, Info, RotateCcw, CheckCircle2, BookOpen,
  Download, FileText,
} from 'lucide-react';
import RevenueAllocationRangeBuilder, { isAllocationValid } from '../components/paymentTerms/RevenueAllocationRangeBuilder';
import EqualMonthlyPreviewPanel from '../components/revenue/EqualMonthlyPreviewPanel';
import toast from 'react-hot-toast';
import { AppModal, ConfirmModal } from '../components/common/AppModal';

const DAY_COUNT_OPTIONS = [
  { value: 'ACTUAL_ACTUAL', label: 'Actual / Actual', desc: 'Actual days in period ÷ actual days in year. Most precise.' },
  { value: 'ACTUAL_365',    label: 'Actual / 365',    desc: 'Actual days ÷ fixed 365. Common for bonds and loans.' },
  { value: 'ACTUAL_360',    label: 'Actual / 360',    desc: 'Actual days ÷ fixed 360. Used in money markets.' },
  { value: 'THIRTY_360',    label: '30 / 360',        desc: 'Each month treated as 30 days. Simplifies bookkeeping.' },
];

function countCalendarMonths(serviceFrom, serviceTo) {
  if (!serviceFrom || !serviceTo) return 0;
  const start = new Date(serviceFrom);
  const end = new Date(serviceTo);
  if (end <= start) return 0;
  let count = 0;
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= endMonth) {
    count += 1;
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return count;
}

function pickPoCatchupSchedule(schedules) {
  const actives = (schedules || []).filter(s => s.status === 'ACTIVE');
  return actives.find(s => s.apportionment_method === 'EQUAL_MONTHLY')
    || actives.sort((a, b) => parseFloat(b.total_amount || 0) - parseFloat(a.total_amount || 0))[0]
    || null;
}

// ═══════════════════════════════════════════════════════════════
// LIST
// ═══════════════════════════════════════════════════════════════
export function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [status, setStatus]     = useState('');
  const [loading, setLoading]   = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    getInvoices({ status: status || undefined })
      .then(r => setInvoices(r.data.invoices || []))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const statuses = ['', 'DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'SENT', 'VIEWED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'DISPUTED', 'CANCELLED'];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-semibold">Invoices</h2>
        <select className="select max-w-xs" value={status} onChange={e => setStatus(e.target.value)}>
          {statuses.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
        </select>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full" style={{tableLayout:'fixed'}}>
          <colgroup>
            <col style={{width:130}}/> {/* Invoice # */}
            <col style={{width:110}}/> {/* SO */}
            <col style={{width:120}}/> {/* Amount */}
            <col style={{width:96}}/>  {/* Due Date */}
            <col style={{width:90}}/>  {/* Status */}
            <col style={{width:110}}/> {/* Approval Mode */}
            <col style={{width:96}}/>  {/* Date */}
            <col style={{width:36}}/>  {/* Arrow */}
          </colgroup>
          <thead>
            <tr>
              {['Invoice #', 'SO', 'Amount', 'Due Date', 'Status', 'Approval Mode', 'Date', ''].map(h => (
                <th key={h} className="table-th">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="table-td text-center text-gray-400">Loading…</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={8} className="table-td text-center text-gray-400 py-8">No invoices found</td></tr>
            ) : invoices.map(inv => (
              <tr key={inv.invoice_id} className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
                <td className="table-td font-mono text-primary-600 font-medium">{inv.invoice_number}</td>
                <td className="table-td text-gray-500 text-sm">{inv.po_number || '—'}</td>
                <td className="table-td font-semibold">
                  {inv.invoice_currency && inv.invoice_currency !== inv.currency
                    ? formatCurrency(inv.foreign_currency_amount ?? inv.total_amount, inv.invoice_currency)
                    : formatCurrency(inv.total_amount, inv.currency)}
                </td>
                <td className="table-td text-sm">{formatDate(inv.due_date)}</td>
                <td className="table-td"><StatusBadge status={inv.status} /></td>
                <td className="table-td text-xs text-gray-500">{inv.approval_mode?.replace(/_/g, ' ')}</td>
                <td className="table-td text-gray-500 text-sm">{formatDate(inv.invoice_date)}</td>
                <td className="table-td"><ChevronRight size={16} className="text-gray-300" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DETAIL
// ═══════════════════════════════════════════════════════════════
export function InvoiceDetail() {
  const { id } = useParams();
  const [invoice, setInvoice]             = useState(null);
  const [revSchedules, setRevSchedules]   = useState([]);
  const [poCatchupSchedule, setPoCatchupSchedule] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [showPayModal, setShowPayModal]   = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showRevModal, setShowRevModal]   = useState(false);
  const [showInvoiceReverseModal, setShowInvoiceReverseModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [invoiceReverseLoading, setInvoiceReverseLoading] = useState(false);
  const [invoiceReverseReason, setInvoiceReverseReason] = useState('');
  const [reverseScheduleId, setReverseScheduleId] = useState(null);
  const [reverseScheduleMode, setReverseScheduleMode] = useState('SINGLE_PERIOD');
  const [reversePeriodMonth, setReversePeriodMonth] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [payAmt, setPayAmt]               = useState('');
  const [payRef, setPayRef]               = useState('');
  const [showBaseAmount, setShowBaseAmount] = useState(false);
  const navigate = useNavigate();
  const { canPostJournal } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const invRes = await getInvoice(id);
      const inv = invRes.data.invoice;
      const [revRes, poRevRes] = await Promise.all([
        getRevenueSchedulesByInvoice(id).catch(() => null),
        inv?.po_id ? getRevenueSchedules({ po_id: inv.po_id }).catch(() => null) : null,
      ]);
      setInvoice(inv);
      setRevSchedules(revRes?.data?.schedules || []);
      setPoCatchupSchedule(pickPoCatchupSchedule(poRevRes?.data?.schedules || []));
    } catch (err) {
      console.error('Failed to load invoice', err);
      toast.error(err.response?.data?.error || 'Failed to load invoice');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleCancel   = async () => {
    setCancelLoading(true);
    try { await cancelInvoice(id, { reason: canDeleteO2c ? 'Deleted before O2C approval' : 'Manually cancelled' }); toast.success(canDeleteO2c ? 'Invoice deleted from PTE and O2C' : 'Invoice cancelled'); setShowCancelConfirm(false); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setCancelLoading(false); }
  };
  const handlePayment  = async (e) => {
    e.preventDefault();
    try { await recordInvoicePayment(id, { amount: parseFloat(payAmt), reference_number: payRef }); toast.success('Payment recorded'); setShowPayModal(false); setPayAmt(''); setPayRef(''); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };
  const handleDispute  = async (e) => {
    e.preventDefault();
    try { await disputeInvoice(id, { reason: disputeReason }); toast.success('Dispute raised'); setShowDisputeModal(false); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };
  const handleInvoiceReverse = async (e) => {
    e.preventDefault();
    const reason = invoiceReverseReason.trim();
    if (!reason) return toast.error('Reversal reason is required');
    setInvoiceReverseLoading(true);
    try {
      const res = await reverseInvoiceToO2c(id, { reason });
      toast.success(
        res.data.idempotent
          ? 'Invoice reversal is already in progress'
          : `Credit Note ${res.data.credit_note_number} queued for O2C approval`
      );
      setShowInvoiceReverseModal(false);
      setInvoiceReverseReason('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to request invoice reversal');
    } finally {
      setInvoiceReverseLoading(false);
    }
  };

  const handlePostEntry = async (scheduleId, periodMonth) => {
    try { await postRevenueEntry(scheduleId, { period_month: periodMonth }); toast.success('Period posted to revenue'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };
  const handleReverseSchedule = async (scheduleId) => {
    try {
      const body = { reversal_mode: reverseScheduleMode };
      if (reverseScheduleMode === 'SINGLE_PERIOD') {
        if (!reversePeriodMonth) return toast.error('Select a period month for single-period reversal');
        body.period_month = reversePeriodMonth;
      }
      await reverseRevenueSchedule(scheduleId, body);
      toast.success('Reversal queued — journals will post to Tally asynchronously');
      setReverseScheduleId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-64 bg-gray-200 rounded-xl"/><div className="h-40 bg-gray-200 rounded-xl"/></div>;
  if (!invoice) return <div className="text-center text-gray-500 py-12">Invoice not found</div>;

  const isO2cPending = ['DRAFT', 'PENDING_APPROVAL'].includes(invoice.status);
  const totalPaid = invoice.receipts?.reduce((s, r) => s + parseFloat(r.amount), 0) || 0;
  const isDebitNote = (cn) => String(cn.note_type || 'CREDIT').toUpperCase() === 'DEBIT';
  const creditNotesOnly = (invoice.credit_notes || []).filter(cn => !isDebitNote(cn));
  const debitNotesOnly = (invoice.credit_notes || []).filter(isDebitNote);
  const isApprovedNote = (cn) => String(cn.o2c_approval_status || '').toUpperCase() === 'APPROVED';
  const approvedCnTotal = creditNotesOnly
    .filter(isApprovedNote)
    .reduce((s, cn) => s + creditNoteTaxableAmount(cn), 0);
  const approvedDnTotal = debitNotesOnly
    .filter(isApprovedNote)
    .reduce((s, cn) => s + creditNoteTaxableAmount(cn), 0);
  const invoiceTaxable = invoiceTaxableAmount(invoice);
  const lineItemTotal = invoice.line_items?.reduce((s, li) => s + parseFloat(li.amount || 0), 0) || 0;
  const cnAlreadyInTotal = lineItemTotal > 0 && (lineItemTotal - invoiceTaxable) >= approvedCnTotal - 0.01;
  const totalCredited = cnAlreadyInTotal ? 0 : approvedCnTotal;
  const totalDebited = approvedDnTotal;
  const displayPaid = totalPaid + totalCredited;
  const balance = Math.max(0, invoiceTaxable - totalPaid - totalCredited + totalDebited);

  const canPay      = canPostJournal && balance > 0.01 && ['SENT', 'VIEWED', 'PARTIALLY_PAID'].includes(invoice.status);
  const canCancel   = isO2cPending || (!['PAID', 'CANCELLED'].includes(invoice.status) && !invoice.o2c_invoice_id);
  const canDeleteO2c = isO2cPending;
  const canDispute  = ['APPROVED', 'SENT', 'VIEWED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE']
    .includes(invoice.status);
  const canReverseInvoice = canPostJournal && invoice.reversal_eligible === true;
  const canSetupRev = ['APPROVED', 'SENT', 'VIEWED', 'PARTIALLY_PAID', 'PAID'].includes(invoice.status);
  // Only count active (non-reversed) schedules — if all are reversed, allow creating a new one
  const activeRevSchedules = revSchedules.filter(s => s.status !== 'REVERSED');
  const hasRevSchedule = activeRevSchedules.length > 0;

  // If this invoice was raised from an unbilled entry, the revenue schedule
  // already exists on the unbilled entry — do NOT prompt to set up again
  const hasUnbilledLink     = !!invoice.unbilled_id;
  const unbilledHasSchedule = !!invoice.unbilled_revenue_schedule_id;
  const revAlreadySetUp     = hasRevSchedule || (hasUnbilledLink && unbilledHasSchedule);
  const usePoLevelRevRec = Boolean(invoice.po_modification_id)
    && !!poCatchupSchedule?.apportionment_method
    && poCatchupSchedule.apportionment_method === 'EQUAL_MONTHLY';
  const hasDeferredCnPost = creditNotesOnly.some(
    cn => (cn.allocation_status === 'PENDING_O2C_POST' || cn.allocation_status === 'PENDING_NEXT_INVOICE')
      && !cn.bridge_credit_note_due_sent_at
  );
  const hasO2cPendingCn = creditNotesOnly.some(
    cn => cn.bridge_credit_note_due_sent_at
      && (cn.allocation_status === 'O2C_PENDING'
        || cn.o2c_approval_status === 'PENDING'
        || !cn.o2c_approval_status)
      && cn.o2c_approval_status !== 'APPROVED'
  );
  const hasPendingCn = hasO2cPendingCn;
  const hasPendingDn = debitNotesOnly.some(
    cn => cn.o2c_approval_status === 'PENDING' || (!cn.o2c_approval_status && cn.bridge_credit_note_due_sent_at)
  );

  const hasForeignCurrency = invoice.invoice_currency
    && invoice.invoice_currency !== invoice.currency;

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={() => navigate('/invoices')} className="hover:text-primary-600">Invoices</button>
        <ChevronRight size={14} />
        <span className="font-medium text-gray-900">{invoice.invoice_number}</span>
      </div>

      {/* Header */}
      <div className="card p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold">{invoice.invoice_number}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              SO:{' '}
              {invoice.po_id && invoice.po_number ? (
                <button
                  type="button"
                  onClick={() => navigate(`/purchase-orders/${invoice.po_id}`)}
                  className="font-semibold text-primary-600 hover:underline"
                >
                  {invoice.po_number}
                </button>
              ) : (
                <strong>{invoice.po_number || '—'}</strong>
              )}
              {invoice.installment_label && <> · Installment: <strong>{invoice.installment_label}</strong></>}
              {invoice.unbilled_entry_number && (
                <>
                  {' · '}
                  <span className="text-blue-600">
                    Unbilled Ref:{' '}
                    <button
                      onClick={() => navigate(`/unbilled-revenue/${invoice.unbilled_id}`)}
                      className="font-semibold underline hover:text-blue-800"
                    >
                      {invoice.unbilled_entry_number}
                    </button>
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <StatusBadge status={invoice.status} />
            {isO2cPending && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ background: 'var(--bt-amber-bg)', color: 'var(--bt-amber)', border: '1px solid #FDE68A' }}>
                Pending O2C approval
              </span>
            )}
            {invoice.status === 'DISPUTED' && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                Open dispute — resolve under Finance → Disputes before modifying the SO
              </span>
            )}
            {isO2cPending && (
              <span className="text-xs text-gray-500 max-w-xs">
                Dispute is available after the invoice is approved in PTE and O2C.
              </span>
            )}
            {canPay      && <button onClick={() => setShowPayModal(true)}    className="btn-primary text-sm"><DollarSign size={14}/> Record Payment</button>}
            {canReverseInvoice && (
              <button
                onClick={() => { setInvoiceReverseReason(''); setShowInvoiceReverseModal(true); }}
                className="btn-secondary text-sm text-amber-700 border-amber-200"
              >
                <RotateCcw size={14}/> Reverse with Credit Note
              </button>
            )}
            {hasDeferredCnPost && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                CN pending — posts to O2C when next invoice is ready
              </span>
            )}
            {hasPendingCn && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ background: 'var(--bt-amber-bg)', color: 'var(--bt-amber)', border: '1px solid #FDE68A' }}>
                CN pending O2C approval
              </span>
            )}
            {hasPendingDn && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ background: 'var(--bt-amber-bg)', color: 'var(--bt-amber)', border: '1px solid #FDE68A' }}>
                DN pending O2C approval
              </span>
            )}
            {canDispute  && <button onClick={() => setShowDisputeModal(true)} className="btn-secondary text-sm text-red-600 border-red-200"><AlertOctagon size={14}/> Dispute</button>}
            {canCancel && (
              <button onClick={() => setShowCancelConfirm(true)} className="btn-secondary text-sm text-red-600 border-red-200">
                <X size={14}/> {canDeleteO2c ? 'Delete' : 'Cancel'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
          {[
            { label: 'Invoice Date',   value: formatDate(invoice.invoice_date) },
            { label: 'Due Date',       value: formatDate(invoice.due_date) },
            { label: 'Approval Mode',  value: invoice.approval_mode?.replace(/_/g, ' ') },
            { label: 'Sent At',        value: formatDateTime(invoice.sent_at) },
            { label: 'Service Period', value: invoice.service_period_from
                ? `${formatDate(invoice.service_period_from)} – ${formatDate(invoice.service_period_to)}`
                : 'Not set' },
            { label: 'Version',        value: invoice.version },
          ].map(f => (
            <div key={f.label}>
              <div className="text-xs text-gray-400">{f.label}</div>
              <div className="font-medium mt-0.5">{f.value || '—'}</div>
            </div>
          ))}
        </div>

        {/* O2C Invoice copy — shown when bridge has synced invoice back from O2C */}
        {(invoice.o2c_invoice_id || invoice.o2c_invoice_url) && (
          <O2CInvoicePanel invoice={invoice} />
        )}

        <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-3 gap-4 text-center">
          {hasForeignCurrency && (
            <div className="col-span-3 flex justify-end mb-1">
              <button
                type="button"
                onClick={() => setShowBaseAmount(v => !v)}
                className="text-xs text-primary-600 hover:underline"
              >
                {showBaseAmount ? 'Show foreign currency' : 'Show base currency equivalent'}
              </button>
            </div>
          )}
          <div>
            <div className="text-xs text-gray-500">Invoice Total</div>
            <div className="text-xl font-bold">{formatCurrency(invoiceTaxable, invoice.currency)}</div>
            <div className="text-xs text-gray-400 mt-0.5">Taxable — GST on O2C/Tally</div>
            {hasForeignCurrency && (
              <div className="text-xs text-gray-400 mt-1">
                {showBaseAmount
                  ? `${formatCurrency(invoice.foreign_currency_amount, invoice.invoice_currency)} @ ${invoice.exchange_rate}`
                  : `Base: ${formatCurrency(invoice.base_currency_amount, invoice.currency)}`}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-gray-500">{totalCredited > 0 ? 'Paid / Credited' : 'Paid'}</div>
            <div className="text-xl font-bold text-green-600">{formatCurrency(displayPaid, invoice.currency)}</div>
            {totalCredited > 0 && totalPaid > 0 && (
              <div className="text-xs text-gray-400 mt-0.5">
                {formatCurrency(totalPaid, invoice.currency)} received · {formatCurrency(totalCredited, invoice.currency)} CN
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-gray-500">Balance Due</div>
            <div className={`text-xl font-bold ${balance > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {formatCurrency(balance, invoice.currency)}
            </div>
          </div>
        </div>
      </div>

      {creditNotesOnly.length > 0 && (
        <div className="card">
          <div className="px-5 py-4 border-b"><h3 className="font-semibold">Credit Notes (O2C)</h3></div>
          {creditNotesOnly.some(cn => cn.allocation_status === 'PENDING_O2C_POST' && !cn.bridge_credit_note_due_sent_at) && (
            <div className="mx-5 mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              Credit note is waiting for the original invoice to sync to O2C before it can be posted.
            </div>
          )}
          {creditNotesOnly.some(cn => cn.settlement_treatment === 'CUSTOMER_CREDIT_BALANCE' && cn.bridge_credit_note_due_sent_at && !cn.allocated_invoice_id) && (
            <div className="mx-5 mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
              Customer credit is in O2C (pending approval). It may later apply against another open invoice (Agst Ref) when one has sufficient balance.
            </div>
          )}
          {creditNotesOnly.some(cn => cn.allocation_status === 'PENDING_NEXT_INVOICE') && (
            <div className="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              No unpaid invoice available for allocation. Credit is stored in customer balance and will auto-apply when the next invoice is created (S5 flow).
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr>{['CN Number', 'Amount (taxable)', 'O2C Status', 'Allocation', 'Issued'].map(h => <th key={h} className="table-th">{h}</th>)}</tr>
            </thead>
            <tbody>
              {creditNotesOnly.map(cn => (
                <tr key={cn.credit_note_id} className="hover:bg-gray-50">
                  <td className="table-td font-mono text-sm">{cn.credit_note_number}</td>
                  <td className="table-td">{formatCurrency(creditNoteTaxableAmount(cn), cn.currency)}</td>
                  <td className="table-td text-sm">
                    {!cn.bridge_credit_note_due_sent_at && cn.allocation_status === 'PENDING_O2C_POST'
                      ? 'Awaiting O2C invoice sync'
                      : (cn.o2c_approval_status || 'PENDING')}
                  </td>
                  <td className="table-td text-sm">
                    {!cn.bridge_credit_note_due_sent_at && cn.allocation_status === 'PENDING_O2C_POST' ? (
                      <span className="text-blue-700">Waiting to post</span>
                    ) : cn.allocation_status === 'O2C_PENDING' || (cn.bridge_credit_note_due_sent_at && cn.o2c_approval_status === 'PENDING') ? (
                      cn.allocated_invoice_id ? (
                        <Link to={`/invoices/${cn.allocated_invoice_id}`} className="text-amber-700 hover:underline">
                          {cn.allocation_message || `Applied against ${invoice.invoice_number} — pending O2C approval`}
                        </Link>
                      ) : (
                        <span className="text-amber-700">
                          {cn.allocation_message || 'Posted to O2C — pending approval'}
                        </span>
                      )
                    ) : cn.allocation_status === 'ALLOCATED' && cn.allocated_invoice_id ? (
                      <Link to={`/invoices/${cn.allocated_invoice_id}`} className={`hover:underline ${['APPROVED', 'PAID'].includes(cn.o2c_approval_status) ? 'text-green-700' : 'text-blue-600'}`}>
                        {cn.allocation_message || 'Allocated'}
                      </Link>
                    ) : (cn.allocation_status === 'ON_ACCOUNT' || cn.allocation_status === 'ORIGINAL_INVOICE')
                      && ['APPROVED', 'PAID'].includes(cn.o2c_approval_status) ? (
                      <span className="text-green-700">{cn.allocation_message || 'O2C approved'}</span>
                    ) : cn.allocation_message ? (
                      <span className={cn.allocation_status === 'PENDING_NEXT_INVOICE' ? 'text-amber-700' : 'text-gray-600'}>
                        {cn.allocation_message}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="table-td text-sm text-gray-500">{formatDateTime(cn.issued_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {debitNotesOnly.length > 0 && (
        <div className="card">
          <div className="px-5 py-4 border-b"><h3 className="font-semibold">Debit Notes (O2C)</h3></div>
          <table className="w-full">
            <thead>
              <tr>{['DN Number', 'Amount (taxable)', 'O2C Status', 'Issued'].map(h => <th key={h} className="table-th">{h}</th>)}</tr>
            </thead>
            <tbody>
              {debitNotesOnly.map(cn => (
                <tr key={cn.credit_note_id} className="hover:bg-gray-50">
                  <td className="table-td font-mono text-sm">{cn.credit_note_number}</td>
                  <td className="table-td">{formatCurrency(creditNoteTaxableAmount(cn), cn.currency)}</td>
                  <td className="table-td text-sm">{cn.o2c_approval_status || 'PENDING'}</td>
                  <td className="table-td text-sm text-gray-500">{formatDateTime(cn.issued_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Line Items */}
      <div className="card">
        <div className="px-5 py-4 border-b"><h3 className="font-semibold">Line Items</h3></div>
        <table className="w-full" style={{tableLayout:'fixed'}}>
          <colgroup>
            <col/>                    {/* Description — flexible */}
            <col style={{width:60}}/> {/* Qty */}
            <col style={{width:70}}/> {/* Unit */}
            <col style={{width:110}}/>{/* Unit Price */}
            <col style={{width:110}}/>{/* Amount */}
          </colgroup>
          <thead>
            <tr>{['Description', 'Qty', 'Unit', 'Unit Price', 'Amount'].map(h => <th key={h} className="table-th">{h}</th>)}</tr>
          </thead>
          <tbody>
            {invoice.line_items?.map(li => (
              <tr key={li.line_id} className="hover:bg-gray-50">
                <td className="table-td">{li.description}</td>
                <td className="table-td text-gray-500">{li.quantity}</td>
                <td className="table-td text-gray-500">{li.unit}</td>
                <td className="table-td">{formatCurrency(li.unit_price, invoice.currency)}</td>
                <td className="table-td font-semibold">{formatCurrency(li.amount, invoice.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── REVENUE RECOGNITION ─────────────────────────────────────── */}
      <div className="card">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-primary-500"/>
            <h3 className="font-semibold">Revenue Recognition</h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Only show Set Up button if no schedule exists anywhere */}
            {canSetupRev && !revAlreadySetUp && !usePoLevelRevRec && (
              <button onClick={() => setShowRevModal(true)} className="btn-primary text-sm">
                <Calendar size={14}/> Set Up Recognition
              </button>
            )}
            {/* Reprocess button for subscription invoices with potentially wrong schedule */}
            {canPostJournal && hasRevSchedule && invoice.term_type === 'SUBSCRIPTION' && (
              <ReprocessRevRecButton invoiceId={invoice.invoice_id} onDone={load}/>
            )}
          </div>
        </div>

        {usePoLevelRevRec && (
          <div className="px-5 py-3 bg-violet-50 border-b border-violet-100 text-xs text-violet-900">
            <p className="font-semibold mb-1">Revenue recognition is managed on the Sales Order (after modification)</p>
            <p className="text-violet-800">
              This SO has a catch-up schedule ({formatDate(poCatchupSchedule.service_period_from)} → {formatDate(poCatchupSchedule.service_period_to)}).
              Post monthly revenue (~catch-up adjusted) from the{' '}
              <button type="button" onClick={() => navigate(`/purchase-orders/${invoice.po_id}`)}
                className="underline font-medium">Sales Order detail page</button>
              {' '}or Finance → Revenue (SO schedule row). Do not set up or post invoice-level revenue here — it would double-count.
            </p>
          </div>
        )}

        {/* If invoice came from unbilled revenue — show the unbilled schedule inline */}
        {hasUnbilledLink && unbilledHasSchedule && (
          <div>
            <div className="px-5 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-3">
              <TrendingUp size={15} className="text-blue-500 flex-shrink-0"/>
              <div className="flex-1 text-xs text-blue-700">
                Schedule set up via unbilled entry{' '}
                <strong>{invoice.unbilled_entry_number}</strong>.
                Post periods below or{' '}
                <button onClick={() => navigate(`/unbilled-revenue/${invoice.unbilled_id}`)}
                  className="underline font-medium">view unbilled entry →</button>
              </div>
            </div>
            <MonthlyEntriesTable
              scheduleId={invoice.unbilled_revenue_schedule_id}
              currency={invoice.currency}
              onPost={handlePostEntry}
              onRefresh={load}
            />
          </div>
        )}

        {/* If unbilled link exists but no schedule on unbilled (unusual) */}
        {hasUnbilledLink && !unbilledHasSchedule && !hasRevSchedule && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 flex items-center gap-2">
            <span>⚠</span>
            This invoice was raised from unbilled entry{' '}
            <strong>{invoice.unbilled_entry_number}</strong> but no revenue schedule was found.
            You can set one up below or via the unbilled entry page.
          </div>
        )}

        {/* Active schedules on the invoice itself */}
        {!hasRevSchedule && !hasUnbilledLink ? (
          <div className="px-5 py-8 text-center">
            <TrendingUp size={32} className="mx-auto text-gray-300 mb-3"/>
            <p className="font-medium text-gray-600">No active revenue recognition schedule</p>
            <p className="text-sm text-gray-400 mt-1 mb-4 max-w-md mx-auto">
              {revSchedules.length > 0
                ? 'Previous schedule was reversed. Create a new one with the correct service period.'
                : 'Define a service period to spread recognition across multiple accounting periods.'}
            </p>
            {canSetupRev ? (
              usePoLevelRevRec ? (
                <p className="text-xs text-violet-700 max-w-md mx-auto">
                  Use the Sales Order revenue schedule — invoice-level setup is disabled for this SO.
                </p>
              ) : (
              <button onClick={() => setShowRevModal(true)} className="btn-primary mx-auto">
                <Calendar size={14}/>
                {revSchedules.length > 0 ? 'Create New Schedule' : 'Set Up Revenue Recognition'}
              </button>
              )
            ) : (
              <p className="text-xs text-gray-400">
                {isO2cPending
                  ? 'Invoice must be approved in O2C before revenue recognition can be set up.'
                  : 'Invoice must be APPROVED before setting up recognition.'}
              </p>
            )}
          </div>
        ) : hasRevSchedule ? (
          activeRevSchedules.map(sched => (
            <RevenueScheduleBlock
              key={sched.schedule_id}
              schedule={sched}
              currency={invoice.currency}
              onPost={handlePostEntry}
              onReverse={(scheduleId) => setReverseScheduleId(scheduleId)}
              onRefresh={load}
              postDisabled={usePoLevelRevRec}
              postDisabledReason="Post from Sales Order — catch-up schedule applies there."
            />
          ))
        ) : null}

        {/* Reversed schedules — collapsed history */}
        {revSchedules.filter(s => s.status === 'REVERSED').length > 0 && (
          <div className="border-t border-gray-100 px-5 py-3">
            <details className="text-xs text-gray-400">
              <summary className="cursor-pointer hover:text-gray-600 select-none">
                {revSchedules.filter(s => s.status === 'REVERSED').length} reversed schedule(s) — click to view history
              </summary>
              <div className="mt-2 space-y-1">
                {revSchedules.filter(s => s.status === 'REVERSED').map(s => (
                  <div key={s.schedule_id} className="flex items-center gap-3 py-1 opacity-60">
                    <span className="font-mono">{s.schedule_id?.slice(0, 8)}…</span>
                    <span>{formatDate(s.service_period_from)} → {formatDate(s.service_period_to)}</span>
                    <span>{formatCurrency(s.total_amount, invoice.currency)}</span>
                    <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">REVERSED</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Payment Receipts */}
      {invoice.receipts?.length > 0 && (
        <div className="card">
          <div className="px-5 py-4 border-b"><h3 className="font-semibold">Payment Receipts</h3></div>
          <table className="w-full" style={{tableLayout:'fixed'}}>
            <colgroup>
              <col style={{width:110}}/>{/* Date */}
              <col style={{width:120}}/>{/* Amount */}
              <col/>                    {/* Reference */}
              <col style={{width:130}}/>{/* Recorded By */}
            </colgroup>
            <thead><tr>{['Date', 'Amount', 'Reference', 'Recorded By'].map(h => <th key={h} className="table-th">{h}</th>)}</tr></thead>
            <tbody>
              {invoice.receipts.map(r => (
                <tr key={r.receipt_id}>
                  <td className="table-td">{formatDateTime(r.received_at)}</td>
                  <td className="table-td font-semibold text-green-600">{formatCurrency(r.amount, invoice.currency)}</td>
                  <td className="table-td font-mono text-sm">{r.reference_number || '—'}</td>
                  <td className="table-td text-gray-500 text-sm">{r.recorded_by || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── LEDGER (Tally-synced entries only) ─────────────────────── */}
      {(invoice.journal_entries?.length > 0 || invoice.pending_journals?.length > 0) && (
        <div className="bt-card">
          <div className="bt-card-header">
            <div className="flex items-center gap-2">
              <BookOpen size={16} style={{ color: 'var(--bt-orange)' }}/>
              <span className="bt-card-title">Accounting Ledger</span>
              <span className="text-xs text-gray-400">(O2C Tally sync)</span>
              {invoice.pending_journals?.length > 0 && (
                <span className="bt-badge bt-badge-amber" style={{ fontSize: 10 }}>
                  {invoice.pending_journals.length} pending Tally
                </span>
              )}
            </div>
          </div>
          {invoice.journal_entries?.length === 0 ? (
            <div className="px-5 py-4 text-sm text-gray-500">
              No entries posted to Tally yet. Entries appear here after O2C Tally sync succeeds.
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 100 }}/>
                <col style={{ width: 140 }}/>
                <col style={{ width: 160 }}/>
                <col style={{ width: 160 }}/>
                <col style={{ width: 110 }}/>
                <col/>
                <col style={{ width: 90 }}/>
              </colgroup>
              <thead>
                <tr>
                  {['Date','Entry Type','Debit A/c','Credit A/c','Amount','Narration','Tally Ref'].map(h => (
                    <th key={h} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoice.journal_entries.map(je => (
                  <tr key={je.journal_id}
                    style={{ background: je.is_reversal ? '#FEF2F2' : undefined }}>
                    <td className="table-td text-xs">{formatDate(je.entry_date)}</td>
                    <td className="table-td">
                      <span style={{
                        fontSize: 11, fontWeight: 500,
                        background: 'var(--bt-gray-100)',
                        color: 'var(--bt-gray-600)',
                        padding: '2px 6px', borderRadius: 4,
                      }}>
                        {je.entry_type?.replace(/_/g,' ')}
                      </span>
                      {je.is_reversal && <span className="ml-1 text-xs" style={{ color: 'var(--bt-red)' }}>(reversal)</span>}
                    </td>
                    <td className="table-td text-xs font-medium">{je.debit_account}</td>
                    <td className="table-td text-xs" style={{ color: 'var(--bt-gray-500)' }}>{je.credit_account}</td>
                    <td className="table-td font-semibold">{formatCurrency(je.amount, je.currency)}</td>
                    <td className="table-td text-xs" style={{ color: 'var(--bt-gray-500)' }}>{je.narration}</td>
                    <td className="table-td text-xs font-mono">{je.tally_voucher_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* Legacy journal section removed — ledger shows Tally-synced only */}
      {false && invoice.journal_entries?.length > 0 && (
        <div className="bt-card">
          <div className="bt-card-header">
            <div className="flex items-center gap-2">
              <BookOpen size={16} style={{ color: 'var(--bt-orange)' }}/>
              <span className="bt-card-title">Journal Entries</span>
              {invoice.journal_entries.some(je => je.status === 'DRAFT') && (
                <span className="bt-badge bt-badge-amber" style={{ fontSize: 10 }}>
                  {invoice.journal_entries.filter(je => je.status === 'DRAFT').length} pending approval
                </span>
              )}
            </div>
            {!isO2cPending && canPostJournal && invoice.journal_entries.some(je => je.status === 'DRAFT') && (
              <span className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>
                As Accounts Officer, you can post draft entries below
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 100 }}/> {/* Date */}
                <col style={{ width: 140 }}/> {/* Entry Type */}
                <col style={{ width: 160 }}/> {/* Dr */}
                <col style={{ width: 160 }}/> {/* Cr */}
                <col style={{ width: 110 }}/> {/* Amount */}
                <col/>                         {/* Narration */}
                <col style={{ width: 90 }}/>  {/* Status */}
                <col style={{ width: 80 }}/>  {/* Action */}
              </colgroup>
              <thead>
                <tr>
                  {['Date','Entry Type','Debit A/c','Credit A/c','Amount','Narration','Status','Action'].map(h => (
                    <th key={h} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoice.journal_entries.map(je => (
                  <tr key={je.journal_id}
                    style={{
                      background: je.status === 'DRAFT' ? '#FFFBEB' : je.is_reversal ? '#FEF2F2' : undefined,
                    }}>
                    <td className="table-td text-xs">{formatDate(je.entry_date)}</td>
                    <td className="table-td">
                      <span style={{
                        fontSize: 11, fontWeight: 500,
                        background: 'var(--bt-gray-100)',
                        color: 'var(--bt-gray-600)',
                        padding: '2px 6px', borderRadius: 4,
                      }}>
                        {je.entry_type?.replace(/_/g,' ')}
                      </span>
                      {je.is_reversal && <span className="ml-1 text-xs" style={{ color: 'var(--bt-red)' }}>(reversal)</span>}
                    </td>
                    <td className="table-td text-xs font-medium">{je.debit_account}</td>
                    <td className="table-td text-xs" style={{ color: 'var(--bt-gray-500)' }}>{je.credit_account}</td>
                    <td className="table-td font-semibold">{formatCurrency(je.amount, je.currency)}</td>
                    <td className="table-td text-xs" style={{ color: 'var(--bt-gray-500)' }}>{je.narration}</td>
                    <td className="table-td">
                      {je.status === 'POSTED'
                        ? <span className="bt-badge bt-badge-green">Posted</span>
                        : <span className="bt-badge bt-badge-amber">Draft</span>
                      }
                      {je.entry_type === 'PAYMENT_RECEIVED' && je.status === 'POSTED' && je.narration?.includes('(O2C)') && (
                        <span className="ml-1 text-xs" style={{ color: 'var(--bt-gray-500)' }}>via O2C</span>
                      )}
                    </td>
                    <td className="table-td">
                      {je.status === 'DRAFT' && !isO2cPending && canPostJournal && je.entry_type !== 'PAYMENT_RECEIVED' ? (
                        <PostJournalButton journalId={je.journal_id} onPosted={load}/>
                      ) : je.status === 'DRAFT' && je.entry_type === 'PAYMENT_RECEIVED' ? (
                        <span className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>Awaiting O2C payment</span>
                      ) : je.status === 'POSTED' && je.approved_by_name ? (
                        <span className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>{je.approved_by_name}</span>
                      ) : je.status === 'POSTED' && je.entry_type === 'PAYMENT_RECEIVED' ? (
                        <span className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>Tally via O2C receipt</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isO2cPending && invoice.journal_entries.some(je => je.status === 'DRAFT') && (
            <div className="px-5 py-3 text-xs" style={{ background: 'var(--bt-amber-bg)', color: 'var(--bt-amber)', borderTop: '1px solid #FDE68A' }}>
              Journal posting unlocks after this invoice is approved in O2C.
            </div>
          )}
          {!isO2cPending && !canPostJournal && invoice.journal_entries.some(je => je.status === 'DRAFT') && (
            <div className="px-5 py-3 text-xs" style={{ background: 'var(--bt-amber-bg)', color: 'var(--bt-amber)', borderTop: '1px solid #FDE68A' }}>
              ⏳ Draft journal entries are awaiting review by Accounts Officer before they are posted to ledger.
            </div>
          )}
        </div>
      )}

      {/* ── MODALS ──────────────────────────────────────────────────── */}

      {/* Revenue Recognition Setup Modal */}
      {showRevModal && (
        <RevenueSetupModal
          invoice={invoice}
          onClose={() => setShowRevModal(false)}
          onCreated={() => { setShowRevModal(false); load(); }}
        />
      )}

      {/* Payment Modal */}
      {showPayModal && (
        <AppModal
          title="Record Payment"
          onClose={() => setShowPayModal(false)}
          maxWidth={440}
          footer={(
            <>
              <button type="button" onClick={() => setShowPayModal(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" form="invoice-pay-form" className="btn-primary flex-1 justify-center">Record Payment</button>
            </>
          )}
        >
          <form id="invoice-pay-form" onSubmit={handlePayment} className="space-y-4">
            <div>
              <label className="label">Amount <span className="text-red-500">*</span></label>
              <input type="number" step="0.01" max={balance} className="input" value={payAmt}
                onChange={e => setPayAmt(e.target.value)} required placeholder={`Max: ${balance.toFixed(2)}`} autoFocus/>
            </div>
            <div>
              <label className="label">Reference Number</label>
              <input className="input" value={payRef} onChange={e => setPayRef(e.target.value)} placeholder="UTR / NEFT ref"/>
            </div>
          </form>
        </AppModal>
      )}

      {showInvoiceReverseModal && (
        <AppModal
          title="Reverse Invoice with Credit Note"
          subtitle="The installment is reopened only after O2C approves the full Credit Note."
          onClose={() => { if (!invoiceReverseLoading) setShowInvoiceReverseModal(false); }}
          maxWidth={520}
          footer={(
            <>
              <button
                type="button"
                onClick={() => setShowInvoiceReverseModal(false)}
                disabled={invoiceReverseLoading}
                className="btn-secondary flex-1 justify-center"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="invoice-reversal-form"
                disabled={invoiceReverseLoading || !invoiceReverseReason.trim()}
                className="btn-primary flex-1 justify-center"
              >
                {invoiceReverseLoading ? 'Queuing…' : 'Submit for O2C Approval'}
              </button>
            </>
          )}
        >
          <form id="invoice-reversal-form" onSubmit={handleInvoiceReverse} className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="flex justify-between gap-4">
                <span>Credit Note taxable amount</span>
                <strong>{formatCurrency(invoice.reversal_taxable_amount, invoice.currency)}</strong>
              </div>
              <div className="flex justify-between gap-4 mt-1">
                <span>SO amount released after approval</span>
                <strong>{formatCurrency(invoice.reversal_release_amount, invoice.currency)}</strong>
              </div>
              <p className="mt-3 text-xs">
                The historical invoice remains cancelled for audit. Its installment returns to Pending and can be invoiced again.
              </p>
            </div>
            <div>
              <label className="label">Reversal reason <span className="text-red-500">*</span></label>
              <textarea
                className="input"
                rows={4}
                value={invoiceReverseReason}
                onChange={e => setInvoiceReverseReason(e.target.value)}
                placeholder="Explain why the full invoice must be reversed and re-invoiced"
                required
                autoFocus
              />
            </div>
          </form>
        </AppModal>
      )}

      {/* Dispute Modal */}
      {showDisputeModal && (
        <AppModal
          title="Raise Dispute"
          onClose={() => setShowDisputeModal(false)}
          maxWidth={440}
          footer={(
            <>
              <button type="button" onClick={() => setShowDisputeModal(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" form="invoice-dispute-form" className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium text-sm">
                <AlertOctagon size={14}/> Raise Dispute
              </button>
            </>
          )}
        >
          <form id="invoice-dispute-form" onSubmit={handleDispute} className="space-y-4">
            <div>
              <label className="label">Reason <span className="text-red-500">*</span></label>
              <textarea className="input" rows={4} value={disputeReason} onChange={e => setDisputeReason(e.target.value)} required autoFocus/>
            </div>
          </form>
        </AppModal>
      )}

      {showCancelConfirm && (
        <ConfirmModal
          title={canDeleteO2c ? 'Delete Invoice' : 'Cancel Invoice'}
          message={canDeleteO2c
            ? 'Delete this invoice from PTE and O2C? Only available while the invoice is not yet approved in O2C.'
            : 'Cancel this invoice? This action cannot be undone from this screen.'}
          confirmLabel={canDeleteO2c ? 'Delete Invoice' : 'Cancel Invoice'}
          onConfirm={handleCancel}
          onClose={() => setShowCancelConfirm(false)}
          loading={cancelLoading}
          danger
        />
      )}

      {reverseScheduleId && (
        <AppModal title="Reverse Revenue Recognition" onClose={() => setReverseScheduleId(null)} maxWidth={480}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Reversal is queued for async Tally posting (not real-time).
            </p>
            <div>
              <label className="label">Reversal mode</label>
              <select className="select" value={reverseScheduleMode}
                onChange={e => setReverseScheduleMode(e.target.value)}>
                <option value="SINGLE_PERIOD">Single period — reverse one posted month</option>
                <option value="SPREAD_REMAINDER">Spread remainder — reverse posted &amp; rebook pending</option>
              </select>
            </div>
            {reverseScheduleMode === 'SINGLE_PERIOD' && (
              <div>
                <label className="label">Period month</label>
                <input type="date" className="input" value={reversePeriodMonth}
                  onChange={e => setReversePeriodMonth(e.target.value)} required />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setReverseScheduleId(null)}>Cancel</button>
              <button type="button" className="btn-primary bg-red-600 hover:bg-red-700"
                onClick={() => handleReverseSchedule(reverseScheduleId)}>
                Queue Reversal
              </button>
            </div>
          </div>
        </AppModal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// REVENUE RECOGNITION SETUP MODAL
// ═══════════════════════════════════════════════════════════════
function RevenueSetupModal({ invoice, onClose, onCreated }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    service_period_from: invoice.service_period_from
      ? invoice.service_period_from.split('T')[0]
      : today,
    service_period_to: invoice.service_period_to
      ? invoice.service_period_to.split('T')[0]
      : '',
    day_count_convention: 'ACTUAL_ACTUAL',
    apportionment_method: 'EQUAL_MONTHLY',
    notes: '',
  });
  const [allocationRanges, setAllocationRanges] = useState([
    { range_start_date: today, range_end_date: '', allocation_percentage: '65', sequence_order: 1 },
    { range_start_date: '', range_end_date: '', allocation_percentage: '35', sequence_order: 2 },
  ]);
  const [preview, setPreview]   = useState(null);
  const [saving, setSaving]     = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const totalAmount = parseFloat(invoice.subtotal ?? invoice.total_amount) || 0;

  // Day-count preview: month labels only (amounts computed server-side on save)
  const calcDayCountPreview = () => {
    if (!form.service_period_from || !form.service_period_to) { setPreview(null); return; }
    const start = new Date(form.service_period_from);
    const end   = new Date(form.service_period_to);
    if (end <= start) { setPreview(null); return; }

    const monthDates = [];
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= endMonth) {
      monthDates.push(new Date(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    setPreview(monthDates.map(m => ({
      label: m.toLocaleString('default', { month: 'short', year: 'numeric' }),
      amount: null,
    })));
  };

  useEffect(() => {
    if (form.apportionment_method === 'DAY_COUNT') calcDayCountPreview();
    else setPreview(null);
  }, [form.service_period_from, form.service_period_to, form.apportionment_method]);

  useEffect(() => {
    if (form.apportionment_method !== 'CUSTOM_PERIOD_ALLOCATION') return;
    if (!form.service_period_from || !form.service_period_to) return;
    setAllocationRanges([
      {
        range_start_date: form.service_period_from,
        range_end_date: form.service_period_to,
        allocation_percentage: '65',
        sequence_order: 1,
      },
      {
        range_start_date: form.service_period_from,
        range_end_date: form.service_period_to,
        allocation_percentage: '35',
        sequence_order: 2,
      },
    ]);
  }, [form.service_period_from, form.service_period_to, form.apportionment_method]);

  const customAllocationOk = form.apportionment_method !== 'CUSTOM_PERIOD_ALLOCATION'
    || isAllocationValid(form.service_period_from, form.service_period_to, allocationRanges);

  const handleSave = async () => {
    if (!form.service_period_from || !form.service_period_to) return toast.error('Both service period dates are required');
    if (new Date(form.service_period_to) <= new Date(form.service_period_from)) return toast.error('End date must be after start date');
    if (!customAllocationOk) return toast.error('Fix custom allocation ranges before saving');
    setSaving(true);
    try {
      const payload = {
        invoice_id: invoice.invoice_id,
        ...form,
      };
      if (form.apportionment_method === 'CUSTOM_PERIOD_ALLOCATION') {
        payload.allocation_ranges = allocationRanges.map((r, i) => ({
          ...r,
          sequence_order: i + 1,
          allocation_percentage: parseFloat(r.allocation_percentage),
        }));
      }
      const result = await createRevenueSchedule(payload);
      const entryCount = result.data.schedules?.[0]?.entries?.length || 0;
      toast.success(`Revenue schedule created — ${entryCount} monthly entries generated`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create revenue schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      title="Set Up Revenue Recognition"
      subtitle={`Invoice ${invoice.invoice_number} — ${formatCurrency(invoice.total_amount, invoice.currency)}`}
      onClose={onClose}
      maxWidth={720}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving || !form.service_period_from || !form.service_period_to || !customAllocationOk}
            className="btn-primary flex-1 justify-center">
            {saving ? 'Creating…' : `Create Schedule (${countCalendarMonths(form.service_period_from, form.service_period_to)} periods)`}
          </button>
        </>
      )}
    >
        <div className="space-y-5">
          {/* Explainer */}
          <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-800">
            <p className="font-medium mb-1 flex items-center gap-1.5"><Info size={14}/> How revenue recognition works</p>
            <p>
              Instead of recognising the full <strong>{formatCurrency(invoice.subtotal ?? invoice.total_amount, invoice.currency)}</strong> in one period,
              you specify a <strong>service start and end date</strong>.
              {form.apportionment_method === 'EQUAL_MONTHLY'
                ? ' The system apportions the amount equally by month (N), prorating partial start months by days.'
                : form.apportionment_method === 'CUSTOM_PERIOD_ALLOCATION'
                ? ' You define custom date ranges and percentage splits.'
                : ' The system spreads the amount across each calendar month using daily proration.'}
              {' '}You post each month&apos;s entry when the accounting period closes.
            </p>
          </div>

          {/* Service period */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                Service Period Start <span className="text-red-500">*</span>
              </label>
              <input type="date" className="input" value={form.service_period_from}
                onChange={e => set('service_period_from', e.target.value)} autoFocus/>
              <p className="text-xs text-gray-400 mt-1">When does the service / contract period begin?</p>
            </div>
            <div>
              <label className="label">
                Service Period End <span className="text-red-500">*</span>
              </label>
              <input type="date" className="input" value={form.service_period_to}
                onChange={e => set('service_period_to', e.target.value)}
                min={form.service_period_from}/>
              <p className="text-xs text-gray-400 mt-1">When does the service / contract period end?</p>
            </div>
          </div>

          {/* Apportionment method */}
          <div>
            <label className="label">Apportionment method</label>
            <div className="flex flex-wrap gap-3 mt-1">
              {[
                { value: 'EQUAL_MONTHLY', label: 'Equal monthly' },
                { value: 'DAY_COUNT', label: 'Day count convention' },
                { value: 'CUSTOM_PERIOD_ALLOCATION', label: 'Custom period allocation' },
              ].map(opt => (
                <label key={opt.value} className={`flex-1 p-3 rounded-xl border cursor-pointer text-sm ${
                  form.apportionment_method === opt.value ? 'border-primary-400 bg-primary-50' : 'border-gray-200'
                }`}>
                  <input type="radio" name="apportion" className="mr-2"
                    checked={form.apportionment_method === opt.value}
                    onChange={() => set('apportionment_method', opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Day count convention — only when day-count apportionment selected */}
          {form.apportionment_method === 'DAY_COUNT' && (
          <div>
            <label className="label">Day Count Convention</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {DAY_COUNT_OPTIONS.map(opt => (
                <label key={opt.value}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    form.day_count_convention === opt.value
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input type="radio" name="dcc" value={opt.value}
                    checked={form.day_count_convention === opt.value}
                    onChange={() => set('day_count_convention', opt.value)}
                    className="mt-0.5"/>
                  <div>
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          )}

          {form.apportionment_method === 'CUSTOM_PERIOD_ALLOCATION' && (
            <RevenueAllocationRangeBuilder
              serviceFrom={form.service_period_from}
              serviceTo={form.service_period_to}
              totalAmount={invoice.total_amount}
              ranges={allocationRanges}
              setRanges={setAllocationRanges}
            />
          )}

          {form.apportionment_method === 'EQUAL_MONTHLY' && totalAmount > 0 && (
            <EqualMonthlyPreviewPanel
              serviceFrom={form.service_period_from}
              serviceTo={form.service_period_to}
              totalAmount={totalAmount}
              currency={invoice.currency}
            />
          )}

          {/* Day-count period preview */}
          {form.apportionment_method === 'DAY_COUNT' && preview && (
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-xs font-semibold text-gray-500 mb-2">
                Monthly entries that will be created ({preview.length} period{preview.length !== 1 ? 's' : ''}):
              </p>
              <div className="flex flex-wrap gap-1.5">
                {preview.map(m => (
                  <span key={m.label} className="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 font-medium">
                    {m.label}{m.amount != null ? ` — ${formatCurrency(m.amount, invoice.currency)}` : ''}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {`${formatCurrency(invoice.subtotal ?? invoice.total_amount, invoice.currency)} will be spread across these ${preview.length} period${preview.length !== 1 ? 's' : ''} using daily proration.`}
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="label">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
            <input className="input" value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="e.g. Annual subscription Apr 2026 – Mar 2027"/>
          </div>

        </div>
    </AppModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// REVENUE SCHEDULE BLOCK (shown on invoice detail)
// ═══════════════════════════════════════════════════════════════
function RevenueScheduleBlock({ schedule, currency, onPost, onReverse, onRefresh, postDisabled, postDisabledReason }) {
  const recognised = parseFloat(schedule.recognised_to_date || 0);
  const deferred   = parseFloat(schedule.deferred_balance || 0);
  const total      = parseFloat(schedule.total_amount || 0);
  const pctDone    = total > 0 ? Math.round((recognised / total) * 100) : 0;

  return (
    <div className="border-b last:border-0">
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-medium text-gray-700">{schedule.line_description || 'Revenue Schedule'}</div>
            <div className="text-xs text-gray-400 mt-0.5">
              Service period: <strong>{formatDate(schedule.service_period_from)}</strong> → <strong>{formatDate(schedule.service_period_to)}</strong>
              <span className="mx-2">·</span>
              Convention: <strong>{schedule.day_count_convention === 'THIRTY_360' ? '30/360' : schedule.day_count_convention?.replace(/_/g, '/')}</strong>
              <span className="mx-2">·</span>
              Status: <strong>{schedule.status}</strong>
            </div>
          </div>
          {schedule.status !== 'REVERSED' && (
            <button onClick={() => onReverse(schedule.schedule_id)}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 border border-gray-200">
              <RotateCcw size={12}/> Reverse
            </button>
          )}
        </div>

        <div className="mt-3 space-y-1.5">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Recognised: <strong className="text-green-600">{formatCurrency(recognised, currency)}</strong></span>
            <span>Deferred: <strong className="text-amber-600">{formatCurrency(deferred, currency)}</strong></span>
            <span>Total: <strong>{formatCurrency(total, currency)}</strong></span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${pctDone}%` }}/>
          </div>
          <div className="text-xs text-gray-400">
            {pctDone}% recognised · {schedule.posted_entries || 0} periods posted · {schedule.pending_entries || 0} pending
          </div>
        </div>
      </div>

      <MonthlyEntriesTable
        scheduleId={schedule.schedule_id}
        currency={currency}
        apportionmentMethod={schedule.apportionment_method}
        servicePeriodFrom={schedule.service_period_from}
        onPost={onPost}
        onRefresh={onRefresh}
        postDisabled={postDisabled}
        postDisabledReason={postDisabledReason}
      />
    </div>
  );
}

// ── Monthly entries (loads its own data) ─────────────────────────
function MonthlyEntriesTable({ scheduleId, currency, apportionmentMethod, servicePeriodFrom, onPost, onRefresh, postDisabled, postDisabledReason }) {
  const [entries, setEntries] = useState(null);
  const [posting, setPosting] = useState(null);

  useEffect(() => {
    import('../services/api').then(({ getRevenueSchedules: _r, ...api }) => {
      // Fetch full schedule detail using getRevenueSchedule (singular)
      fetch(`${window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL}/revenue-schedules/${scheduleId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.json())
        .then(d => setEntries(d.schedule?.entries || []))
        .catch(() => setEntries([]));
    });
  }, [scheduleId]);

  const handlePost = async (entry) => {
    setPosting(entry.period_month);
    try {
      await onPost(scheduleId, entry.period_month);
      // Refresh entries
      fetch(`${window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL}/revenue-schedules/${scheduleId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.json())
        .then(d => setEntries(d.schedule?.entries || []));
      onRefresh();
    } finally {
      setPosting(null);
    }
  };

  if (!entries) return <div className="px-5 pb-4 text-xs text-gray-400 animate-pulse">Loading entries…</div>;

  return (
    <div className="overflow-x-auto border-t border-gray-50">
      <table className="w-full" style={{tableLayout:'fixed'}}>
        <colgroup>
          <col style={{width:100}}/> {/* Period */}
          <col style={{width:55}}/>  {/* Days */}
          <col style={{width:90}}/>  {/* Proration % */}
          <col style={{width:120}}/> {/* Amount */}
          <col style={{width:140}}/> {/* Accounting Period */}
          <col style={{width:90}}/>  {/* Status */}
          <col style={{width:80}}/>  {/* Action */}
        </colgroup>
        <thead>
          <tr className="bg-gray-50">
            {['Period', 'Days', 'Proration %', 'Amount', 'Accounting Period', 'Status', 'Action'].map(h => (
              <th key={h} className="table-th text-xs">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => {
            const catchup = parseFloat(entry.catchup_adjustment_amount || 0);
            const netAmount = parseFloat(entry.recognised_amount || 0);
            const baseAmount = catchup > 0
              ? Math.round((netAmount + catchup) * 100) / 100
              : netAmount;
            const isEqualMonthly = apportionmentMethod === 'EQUAL_MONTHLY';
            const isLast = idx === entries.length - 1;
            const startDay = servicePeriodFrom ? parseInt(String(servicePeriodFrom).split('T')[0].split('-')[2], 10) : 1;
            const isPartialStart = isEqualMonthly && idx === 0 && startDay > 1;
            return (
            <tr key={entry.entry_id} className={
              entry.status === 'POSTED' ? 'bg-green-50/30'
                : entry.status === 'REVERSED' || entry.status === 'CANCELLED' ? 'bg-gray-50 opacity-60'
                : 'hover:bg-gray-50'
            }>
              <td className="table-td text-sm font-medium">
                {entry.period_label}
                {isEqualMonthly && isLast && (
                  <span
                    className="ml-1.5 inline-flex px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium align-middle"
                    title="This period absorbs rounding differences to ensure the schedule total equals the contract amount exactly."
                  >
                    Balance
                  </span>
                )}
                {isPartialStart && (
                  <div className="text-[10px] text-violet-600 font-normal">
                    Prorated — {entry.days_in_period} days
                  </div>
                )}
              </td>
              <td className="table-td text-xs text-gray-500">{entry.days_in_period}</td>
              <td className="table-td text-xs text-gray-500">{(parseFloat(entry.proration_factor) * 100).toFixed(2)}%</td>
              <td className="table-td font-semibold">
                <div>{formatCurrency(netAmount, currency)}</div>
                {catchup > 0.009 && (
                  <div className="text-[10px] text-violet-600 font-normal mt-0.5" title="SPREAD catch-up from parked posted revenue">
                    {formatCurrency(baseAmount, currency)} − {formatCurrency(catchup, currency)} catch-up
                  </div>
                )}
              </td>
              <td className="table-td text-xs font-mono text-gray-500">{entry.accounting_period}</td>
              <td className="table-td">
                {entry.status === 'POSTED' ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                    <CheckCircle2 size={12}/> Posted
                  </span>
                ) : entry.status === 'REVERSED' ? (
                  <span className="text-xs text-gray-400">Reversed</span>
                ) : entry.status === 'CANCELLED' ? (
                  <span className="text-xs text-gray-400 line-through">Cancelled</span>
                ) : (
                  <span className="text-xs text-amber-600 font-medium">Pending</span>
                )}
              </td>
              <td className="table-td">
                {entry.status === 'PENDING' && (
                  postDisabled ? (
                    <span className="text-[10px] text-violet-600" title={postDisabledReason}>SO schedule</span>
                  ) : (
                  <button
                    onClick={() => handlePost(entry)}
                    disabled={posting === entry.period_month}
                    className="text-xs px-2.5 py-1 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200 font-medium"
                  >
                    {posting === entry.period_month ? 'Posting…' : 'Post'}
                  </button>
                  )
                )}
              </td>
            </tr>
          );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Post Journal Button ──────────────────────────────────────────
function PostJournalButton({ journalId, onPosted }) {
  const [posting, setPosting] = React.useState(false);
  const handlePost = async () => {
    setPosting(true);
    try {
      await approveJournalEntry(journalId);
      toast.success('Journal entry posted to ledger');
      onPosted();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post');
    } finally { setPosting(false); }
  };
  return (
    <button onClick={handlePost} disabled={posting}
      className="bt-btn bt-btn-sm bt-btn-primary"
      style={{ fontSize: 11, padding: '3px 10px' }}>
      {posting ? 'Posting…' : 'Post'}
    </button>
  );
}

// ── Reprocess Subscription Revenue Recognition Button ────────────
// Shows on subscription invoices where the schedule may have been created
// with wrong billing cycle (e.g. MONTHLY instead of QUARTERLY)
function ReprocessRevRecButton({ invoiceId, onDone }) {
  const [running, setRunning] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const handle = async () => {
    setRunning(true);
    try {
      const res = await reprocessSubscriptionRevRec(invoiceId);
      toast.success(
        `Revenue recognition reprocessed. Service period: ${res.data.service_period_from} → ${res.data.service_period_to}. ${res.data.posted_entries || 0} period(s) auto-posted.`
      );
      setShowConfirm(false);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reprocess');
    } finally { setRunning(false); }
  };
  return (
    <>
      <button onClick={() => setShowConfirm(true)} disabled={running}
      className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border font-medium"
      style={{ background: '#FFF7ED', color: '#C2410C', borderColor: '#FED7AA', opacity: running ? 0.7 : 1 }}
      title="Recreate revenue recognition with correct billing cycle (use if schedule was created incorrectly)">
      <RotateCcw size={12}/> {running ? 'Reprocessing…' : 'Fix Rev Rec'}
    </button>
      {showConfirm && (
        <ConfirmModal
          title="Fix Revenue Recognition"
          message="This will reverse the existing revenue recognition schedule and recreate it with the correct billing cycle. Continue?"
          confirmLabel="Reprocess"
          onConfirm={handle}
          onClose={() => setShowConfirm(false)}
          loading={running}
        />
      )}
    </>
  );
}

// ── O2C Invoice Copy Panel ────────────────────────────────────────
// Shows when bridge has synced back the O2C invoice PDF into PTE.
// Sales team can view and download the PDF without leaving PTE.
function O2CInvoicePanel({ invoice }) {
  const [pdfAttachment, setPdfAttachment] = React.useState(null);
  const [loadingPdf, setLoadingPdf]       = React.useState(true);
  const [previewUrl, setPreviewUrl]       = React.useState(null);

  React.useEffect(() => {
    getAttachments('invoice', invoice.invoice_id)
      .then(res => {
        const pdfs = (res.data.attachments || []).filter(a =>
          a.file_type === 'application/pdf' || a.file_name?.endsWith('.pdf')
        );
        setPdfAttachment(pdfs[0] || null);
      })
      .catch(() => setPdfAttachment(null))
      .finally(() => setLoadingPdf(false));
  }, [invoice.invoice_id]);

  // Decode base64 → object URL for inline preview
  const handlePreview = async () => {
    if (previewUrl) { setPreviewUrl(null); return; } // toggle off
    try {
      const res    = await downloadAttachment(pdfAttachment.attachment_id);
      const bytes  = Uint8Array.from(atob(res.data.file_data), c => c.charCodeAt(0));
      const blob   = new Blob([bytes], { type: 'application/pdf' });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch { toast.error('Failed to load PDF preview'); }
  };

  const handleDownload = async () => {
    try {
      const res    = await downloadAttachment(pdfAttachment.attachment_id);
      const bytes  = Uint8Array.from(atob(res.data.file_data), c => c.charCodeAt(0));
      const blob   = new Blob([bytes], { type: 'application/pdf' });
      const url    = URL.createObjectURL(blob);
      const a      = document.createElement('a');
      a.href       = url;
      a.download   = pdfAttachment.file_name
        || `invoice-${invoice.o2c_invoice_number || invoice.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  // Nothing to show until we know whether a PDF exists
  if (loadingPdf) return null;
  if (!pdfAttachment && !invoice.o2c_invoice_number) return null;

  return (
    <div className="rounded-xl border mt-4 overflow-hidden"
      style={{ borderColor: 'var(--bt-orange-border)' }}>

      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 flex-wrap gap-2"
        style={{ background: 'var(--bt-orange-light)' }}>
        <div className="flex items-center gap-2 flex-wrap">
          <FileText size={15} style={{ color: 'var(--bt-orange)' }}/>
          <span className="font-semibold text-sm" style={{ color: 'var(--bt-orange)' }}>
            Customer Invoice
          </span>
          {invoice.o2c_invoice_number && (
            <span className="font-mono text-sm" style={{ color: 'var(--bt-gray-600)' }}>
              {invoice.o2c_invoice_number}
            </span>
          )}
          {invoice.o2c_sent_at && (
            <span className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>
              · Sent {new Date(invoice.o2c_sent_at).toLocaleDateString('en-IN',
                { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          )}
          {invoice.tally_voucher_id && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ background: '#F0FDF4', color: '#15803D' }}>
              ✓ Tally {invoice.tally_voucher_id}
            </span>
          )}
        </div>

        {pdfAttachment ? (
          <div className="flex items-center gap-2">
            <button onClick={handlePreview}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium border"
              style={{ background: '#fff', color: 'var(--bt-orange)', borderColor: 'var(--bt-orange-border)' }}>
              <FileText size={12}/>
              {previewUrl ? 'Close Preview' : 'Preview PDF'}
            </button>
            <button onClick={handleDownload}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium"
              style={{ background: 'var(--bt-orange)', color: '#fff' }}>
              <Download size={12}/> Download PDF
            </button>
          </div>
        ) : (
          <span className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>
            PDF will appear here once O2C sends it
          </span>
        )}
      </div>

      {/* Inline PDF preview */}
      {previewUrl && (
        <div style={{ height: 600, background: '#525659' }}>
          <iframe
            src={previewUrl}
            title="Invoice PDF"
            width="100%"
            height="100%"
            style={{ border: 'none', display: 'block' }}
          />
        </div>
      )}
    </div>
  );
}
